/** Thứ tự dòng trên bảng hàng hóa của đơn — sản phẩm và vật tư XEN KẼ nhau.
 *
 *  Bảng hàng hóa của đơn ghép hai nguồn tách rời: dòng sản phẩm (`items`) và
 *  dòng vật tư (packaging draft). Trước đây UI render hai KHỐI cứng — hết sản
 *  phẩm rồi mới tới vật tư — nên người bán thêm vật tư xong bấm "Thêm dòng" thì
 *  dòng sản phẩm mới lại chèn TRÊN khối vật tư (FD #358).
 *
 *  Cách chữa: cả hai loại dòng dùng CHUNG một dãy số thứ tự trong phạm vi một
 *  đơn (`row_seq` lúc soạn form, `sort_order` khi đã lưu xuống DB). Module này
 *  giữ toàn bộ luật sắp xếp + đánh số để form tạo đơn, form sửa đơn và màn chi
 *  tiết dùng chung một nguồn sự thật.
 *
 *  Dòng thiếu số thứ tự (đơn cũ trước migration, POS, import KiotViet) fallback
 *  đúng cách hiển thị trước đây: sản phẩm 0..n-1, vật tư nối tiếp ngay sau —
 *  cùng công thức với backend (`sales_order.itemSortOrder` /
 *  `packagingSortOrder`) và với backfill của migration.
 */

/** Một dòng trên bảng hàng hóa, trỏ về vị trí trong mảng gốc của nó. */
export type OrderLineRef =
    { kind: 'item'; index: number } | { kind: 'material'; index: number }

/** Số thứ tự hiệu lực của một dòng: số đã có, hoặc fallback theo vị trí. */
function seqOf(seq: number | undefined | null, fallback: number): number {
    return typeof seq === 'number' && Number.isFinite(seq) ? seq : fallback
}

/**
 * Trộn dòng sản phẩm và dòng vật tư (thêm tay / order-level) thành MỘT danh
 * sách theo đúng thứ tự hiển thị.
 *
 * `itemSeqs` / `materialSeqs` là số thứ tự của từng dòng theo đúng vị trí trong
 * mảng gốc; `undefined` = dòng chưa có số.
 *
 * Trùng số thì sản phẩm đứng trước vật tư (sort ổn định + nạp sản phẩm trước) —
 * quan trọng cho đơn cũ có toàn `sort_order = 0` và cho vật tư định mức mang số
 * của dòng sản phẩm cha.
 */
export function mergeOrderLines(
    itemSeqs: ReadonlyArray<number | undefined | null>,
    materialSeqs: ReadonlyArray<number | undefined | null>,
): OrderLineRef[] {
    const rows: Array<{ ref: OrderLineRef; seq: number }> = []
    itemSeqs.forEach((seq, index) => {
        rows.push({ ref: { kind: 'item', index }, seq: seqOf(seq, index) })
    })
    materialSeqs.forEach((seq, index) => {
        rows.push({
            ref: { kind: 'material', index },
            seq: seqOf(seq, itemSeqs.length + index),
        })
    })
    // Array.prototype.sort ổn định (ES2019) → dòng trùng số giữ nguyên thứ tự
    // nạp vào: sản phẩm trước, vật tư sau.
    return rows.sort((a, b) => a.seq - b.seq).map((r) => r.ref)
}

/** Dòng sản phẩm khi đánh số gửi lên API. `key` = khoá dòng đơn (field.id lúc
 *  tạo, order_item.id lúc sửa) để vật tư định mức tìm được cha. */
export interface SortableItemLine {
    key: string
    row_seq?: number
}

/** Dòng vật tư khi đánh số gửi lên API. `line_key` rỗng = vật tư thêm tay
 *  (ngang hàng sản phẩm); có giá trị = vật tư định mức của dòng sản phẩm đó. */
export interface SortableMaterialLine {
    line_key: string
    row_seq?: number
}

/**
 * Đánh lại số thứ tự 0..n-1 theo đúng thứ tự hiển thị để gửi lên API
 * (`sort_order`). Trả về hai mảng song song với `items` / `materials` đầu vào.
 *
 * Vật tư định mức (`line_key` trỏ tới một dòng sản phẩm) nhận luôn số của dòng
 * cha vì nó luôn render ngay dưới cha. Vật tư trỏ tới dòng đã bị xoá thì xếp
 * cuối bảng thay vì rơi về đầu.
 */
export function buildLineSortOrders({
    items,
    materials,
}: {
    items: ReadonlyArray<SortableItemLine>
    materials: ReadonlyArray<SortableMaterialLine>
}): { itemSortOrders: number[]; materialSortOrders: number[] } {
    const manualIndexes: number[] = []
    materials.forEach((mat, index) => {
        if (!mat.line_key) manualIndexes.push(index)
    })

    const merged = mergeOrderLines(
        items.map((it) => it.row_seq),
        manualIndexes.map((i) => materials[i].row_seq),
    )

    const itemSortOrders = new Array<number>(items.length).fill(0)
    const materialSortOrders = new Array<number>(materials.length).fill(0)
    const sortByItemKey = new Map<string, number>()

    merged.forEach((ref, position) => {
        if (ref.kind === 'item') {
            itemSortOrders[ref.index] = position
            sortByItemKey.set(items[ref.index].key, position)
        } else {
            materialSortOrders[manualIndexes[ref.index]] = position
        }
    })

    materials.forEach((mat, index) => {
        if (!mat.line_key) return
        const parent = sortByItemKey.get(mat.line_key)
        materialSortOrders[index] = parent ?? merged.length
    })

    return { itemSortOrders, materialSortOrders }
}
